/**
 * Canonical items API client (PR-2 of the abilities cleanup).
 *
 * Wraps the new server endpoints introduced in PR-1:
 *   POST    /api/items/:type/install/:slug
 *   DELETE  /api/items/:type/:id
 *   PATCH   /api/items/:type/:id
 *   POST    /api/items/:type/:id/update
 *   POST    /api/items/:type
 *   GET     /api/items/:type/mine
 *
 * The legacy `skills.ts` / `agents.ts` modules preserve their existing
 * slug-based exported signatures and delegate down to these helpers, so
 * consumers don't need to change. Where the legacy contract takes a slug
 * but the new endpoint takes an item id, the wrappers below issue an extra
 * `GET /api/items/:type/mine` to resolve.
 */
import type { ItemDetail } from "@bash/shared";
import { apiUrl } from "./base";
import { authedFetch } from "./client";

export type ItemType = "skill" | "agent" | "hook" | "mcp";

/**
 * Shape returned by `GET /api/items/:type/mine`. Mirrors the server's
 * projection in `apps/server/src/handlers/items-routes.handler.ts`.
 */
export interface MineItem {
  id: string;
  slug: string;
  scope: "system" | "marketplace" | "user";
  version: number;
  installed_version: number | null;
  content_checksum: string;
  metadata: Record<string, unknown>;
  /**
   * MCP items (`type='mcp'`) carry a connection_config with the transport, url,
   * and the referenced secret KEY NAMES (never values). Null for other item
   * types, whose tables have no such column.
   */
  connection_config: Record<string, unknown> | null;
  has_local_edits: boolean;
  update_available: boolean;
  bundling_ability_slugs: string[];
}

export interface InstallItemResult {
  id: string;
  slug: string;
  installed_version: number;
}

/** POST /api/items/:type/install/:slug — install a system/marketplace item. */
export async function installItem(
  type: ItemType,
  slug: string
): Promise<InstallItemResult> {
  const res = await authedFetch(
    `/api/items/${type}/install/${encodeURIComponent(slug)}`,
    {
      method: "POST",
      // The server doesn't read a body, but Fastify rejects POSTs that declare
      // Content-Type: application/json with no body. Send an empty object.
      body: "{}",
    }
  );
  return (await res.json()) as InstallItemResult;
}

/** GET /api/items/:type/mine — list the caller's user-scope items. */
export async function listMyItems(type: ItemType): Promise<MineItem[]> {
  const res = await authedFetch(`/api/items/${type}/mine`);
  const json = await res.json();
  return (json.items ?? []) as MineItem[];
}

/** DELETE /api/items/:type/:id — uninstall a user item by id. */
export async function deleteItem(type: ItemType, id: string): Promise<void> {
  await authedFetch(`/api/items/${type}/${id}`, { method: "DELETE" });
}

/**
 * Result of forking a non-user item into the caller's Studio ability. The
 * returned id is the new user-scope row that PATCH /api/items/:type/:id
 * can safely mutate.
 */
export interface ForkItemResult {
  id: string;
  /**
   * The slug landed on the fork. Usually identical to the source slug but
   * if a user-scope item with the same slug already existed under the
   * caller's account, the server suffixes `-fork-<n>` to disambiguate.
   * Display this in the redirect URL so the user can tell which fork is
   * theirs.
   */
  slug: string;
}

/**
 * POST /api/items/:type/:id/fork — clone a marketplace/system item into the
 * caller's Studio so the next PATCH lands on a row the user owns. Implements
 * the "lazy clone on first edit" half of the abilities install semantics:
 * the source row is untouched (still visible to every other user); only the
 * caller sees the new fork. Use this when the Edit button is pressed on a
 * non-user item — the UI should then redirect to the returned id's edit page.
 */
export async function forkItem(
  type: ItemType,
  id: string
): Promise<ForkItemResult> {
  const res = await authedFetch(`/api/items/${type}/${id}/fork`, {
    method: "POST",
    body: "{}",
  });
  return (await res.json()) as ForkItemResult;
}

/**
 * Resolve a slug to the caller's user-scope item id via `GET /mine`.
 * Returns `null` if the user has no matching install.
 *
 * Used by the legacy slug-based wrappers in skills.ts / agents.ts that need
 * to translate to the id-based DELETE endpoint.
 */
export async function findMyItemBySlug(
  type: ItemType,
  slug: string
): Promise<MineItem | null> {
  const items = await listMyItems(type);
  return items.find((i) => i.slug === slug) ?? null;
}

/** Fetch a single item (content + inline files + editable flag) by slug. Null on 404. */
export async function getItemBySlug(
  type: ItemType,
  slug: string,
  accessToken: string
): Promise<ItemDetail | null> {
  const res = await fetch(
    apiUrl(`/api/items/${type}/by-slug/${encodeURIComponent(slug)}`),
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
  if (res.status === 404) return null;
  if (!res.ok)
    throw new Error(`Failed to load ${type} "${slug}" (${res.status})`);
  const data = await res.json();
  return (data.item ?? null) as ItemDetail | null;
}

/** Body for POST /api/items/:type — create a new user-scope item. */
export interface CreateItemBody {
  slug: string;
  content: string;
  metadata?: Record<string, unknown>;
  files?: Array<{ path: string; content: string }>;
  ability_id?: string;
}

/** POST /api/items/:type — create a new user-scope item. */
export async function createItem(
  type: ItemType,
  body: CreateItemBody,
  accessToken: string
): Promise<{ id: string; slug: string }> {
  const res = await fetch(apiUrl(`/api/items/${type}`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(
      (await res.json().catch(() => ({}))).error ||
        `Failed to create ${type} (${res.status})`
    );
  }
  return res.json();
}

/** Body for PATCH /api/items/:type/:id — update an existing user-scope item. */
export interface UpdateItemBody {
  content?: string;
  metadata?: Record<string, unknown>;
  files?: Array<{ path: string; content: string }>;
}

/** PATCH /api/items/:type/:id — update an existing user-scope item by id. */
export async function updateItem(
  type: ItemType,
  id: string,
  body: UpdateItemBody,
  accessToken: string
): Promise<void> {
  const res = await fetch(apiUrl(`/api/items/${type}/${id}`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(
      (await res.json().catch(() => ({}))).error ||
        `Failed to update ${type} (${res.status})`
    );
  }
}
